import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import OptReportsByParentJobIdQuery, {
  OptimizationReportsByParentJobIdResponse,
} from '@graphql/queries/OptReportsByParentJobIdQuery';
import OptVersionsByReportIdQuery, {
  OptVersionsByReportIdResponse,
} from '@graphql/queries/OptVersionsByReportIdQuery';
import { uniq } from 'lodash';
import { constructArtifactPath } from 'utils/conchUtils';

import { parseParams } from './useParentDetails';
import { asArray, asScalar } from '../model/templates';
import { ParentReport } from '../model/types';

export interface OptimizationRun {
  reportId: string;
  startedAt: string;
  passedQa: boolean;
  projectName: string;
  artifactPath: string;
  isMixAndMatch: boolean;
}

/** Successful optimization runs of `parent` that optimized `targetProduct`. */
export const useOptimizationRuns = (
  parent: ParentReport | null,
  targetProduct: string | null,
) => {
  const { data, loading, error } =
    useQuery<OptimizationReportsByParentJobIdResponse>(
      OptReportsByParentJobIdQuery,
      {
        skip: !parent,
        variables: { parentJobId: parent?.jobId, orderBy: 'STARTED_AT_DESC' },
      },
    );

  const parsed = useMemo(
    () =>
      (data?.allReportJobs.nodes ?? []).map((r) => ({
        report: r,
        params: parseParams(r.params) ?? {},
      })),
    [data],
  );

  /** Number of runs per optimized product, so pickers can steer users to products with runs. */
  const runCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    parsed.forEach(({ params }) =>
      uniq(asArray<string>(params.products_to_opt)).forEach((p) => {
        counts[p] = (counts[p] ?? 0) + 1;
      }),
    );
    return counts;
  }, [parsed]);

  const runs = useMemo<OptimizationRun[]>(() => {
    if (!targetProduct) return [];
    return parsed
      .filter(({ params }) =>
        asArray<string>(params.products_to_opt).includes(targetProduct),
      )
      .map(({ report, params }) => {
        const projectName = asScalar<string>(
          params.project_name,
          report.projectName,
        );
        return {
          reportId: report.reportId,
          startedAt: report.startedAt,
          passedQa: !!report.passedQa,
          projectName,
          artifactPath: constructArtifactPath(
            report.startedAt,
            report.clientName,
            projectName,
            report.targetGroupName,
          ),
          isMixAndMatch: asArray(params.mix_and_match).length > 0,
        };
      });
  }, [parsed, targetProduct]);

  return { runs, runCounts, loading, error };
};

/** Version numbers available in an optimization run's summaries, sorted numerically. */
export const useOptimizationVersions = (reportId: string, skip = false) => {
  const { data, loading, error } = useQuery<OptVersionsByReportIdResponse>(
    OptVersionsByReportIdQuery,
    { skip, variables: { reportId } },
  );
  const versions = useMemo(
    () =>
      uniq(
        (data?.allReportSummaries.nodes ?? []).map((n) => String(n.version)),
      ).sort((a, b) => Number(a) - Number(b) || a.localeCompare(b)),
    [data],
  );
  return { versions, loading, error };
};
